import { eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, X, ChefHat, Bell, Clock, Utensils, Printer, Search, Users, Pencil, Ban } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import KitchenTicket from "@/components/dashboard/KitchenTicket";
import OrderDetailDrawer from "@/components/dashboard/OrderDetailDrawer";
import TableOverviewDrawer from "@/components/dashboard/TableOverviewDrawer";
import OrderEditDialog from "@/components/common/OrderEditDialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

function elapsed(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  const m = Math.floor(s / 60);
  return m < 1 ? "à l'instant" : `il y a ${m} min`;
}

const STATUS_META = {
  NOUVELLE: { label: "Nouvelle", color: "text-[#E7C97A]", ring: "border-[#C69C4A]", chip: "bg-[#C69C4A]/15" },
  ACCEPTEE: { label: "Acceptée", color: "text-[#E7C97A]", ring: "border-[#C69C4A]", chip: "bg-[#C69C4A]/15" },
  EN_PREPARATION: { label: "En préparation", color: "text-blue-200", ring: "border-blue-400", chip: "bg-blue-400/15" },
  PRETE: { label: "Prête", color: "text-emerald-200", ring: "border-emerald-400", chip: "bg-emerald-400/15" },
  SERVIE: { label: "Servie", color: "text-stone-400", ring: "border-stone-600", chip: "bg-white/5" },
  REFUSEE: { label: "Refusée", color: "text-red-300", ring: "border-red-500", chip: "bg-red-500/15" },
};

function timeAgo(iso) {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `il y a ${diff}s`;
  const m = Math.floor(diff / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  return `il y a ${h} h`;
}

const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

function OrderCard({ order, onStatus, isNew, onTicket, onOpen, onTableView, onTakeService, onEdit, onCancel, readyAlertMin = 3 }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const meta = STATUS_META[order.status];
  const num = order.order_number.replace("#", "");
  const code = order.order_code || order.order_number;
  const readySec = order.status === "PRETE" && order.ready_at ? Math.max(0, Math.floor((Date.now() - new Date(order.ready_at).getTime()) / 1000)) : 0;
  const taken = !!order.taken_for_service_at;
  const readyLate = order.status === "PRETE" && !taken && readySec >= (readyAlertMin || 3) * 60;
  const mmss = `${Math.floor(readySec / 60)}:${String(readySec % 60).padStart(2, "0")}`;

  return (
    <div
      data-testid={`order-card-${num}`}
      role="button"
      onClick={() => onOpen(order)}
      className={`relative rounded-2xl bg-[#111F1A] border-2 ${meta.ring} p-4 flex flex-col cursor-pointer hover:border-[#C69C4A] transition-colors ${isNew ? "animate-flash-new animate-pop-in" : ""} ${readyLate ? "ring-2 ring-red-500 animate-flash-new" : ""}`}
    >
      <div className="flex items-start justify-between mb-3">
        {order.is_addition && <span data-testid={`order-addition-badge-${num}`} className="absolute -top-2 left-4 rounded-full bg-[#C69C4A] text-[#0C1713] text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5">Ajout · {order.parent_order_number}</span>}
        <div>
          <p className="font-serif-display text-2xl text-stone-100 leading-none">Table {order.table_number}</p>
          <p className="text-xs text-stone-500 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> {timeAgo(order.created_at)} · {code}</p>
          {order.guest_name && <p className="text-xs text-stone-400 mt-0.5">👤 {order.guest_name}</p>}
          {order.accepted_by && <p className="text-[11px] text-emerald-400/90 mt-0.5">✓ Accepté par {order.accepted_by}</p>}
          {order.served_by && <p className="text-[11px] text-stone-400 mt-0.5">🍽 Servi par {order.served_by}</p>}
          {order.status === "REFUSEE" && order.refused_by && <p className="text-[11px] text-red-400/90 mt-0.5">✕ {order.cancelled ? "Annulée" : "Refusée"} par {order.refused_by}</p>}
          {order.modified_at && !["SERVIE", "REFUSEE"].includes(order.status) && <p data-testid={`order-modified-${order.id}`} className="text-[11px] text-amber-300 mt-0.5">✎ Modifiée{order.modified_by ? ` par ${order.modified_by}` : ""}</p>}
        </div>
        <span className={`text-sm font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full ${meta.color} ${meta.chip}`}>{meta.label}</span>
      </div>
      {onEdit && !["SERVIE", "REFUSEE"].includes(order.status) && !order.paid && (
        <div className="flex gap-2 mb-2">
          <Button data-testid={`btn-edit-${num}`} onClick={stop(() => onEdit(order))} variant="outline" size="sm" className="flex-1 rounded-xl border-[#1B3028] text-stone-200 bg-transparent h-9"><Pencil className="w-3.5 h-3.5 mr-1" /> Modifier</Button>
          {order.status !== "NOUVELLE" && <Button data-testid={`btn-cancel-${num}`} onClick={stop(() => onCancel(order))} variant="outline" size="sm" className="flex-1 rounded-xl border-red-500/40 text-red-400 hover:bg-red-500/10 bg-transparent h-9"><Ban className="w-3.5 h-3.5 mr-1" /> Annuler</Button>}
        </div>
      )}

      <div className="flex items-center gap-3 mb-2">
        <button data-testid={`table-view-btn-${num}`} onClick={stop(() => onTableView(order))} className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-[#C69C4A] font-semibold">
          <Users className="w-3.5 h-3.5" /> Toute la table
        </button>
        {order.status !== "NOUVELLE" && (
          <button data-testid={`btn-ticket-${num}`} onClick={stop(() => onTicket(order))} className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-[#C69C4A] font-semibold">
            <Printer className="w-3.5 h-3.5" /> Ticket cuisine
          </button>
        )}
      </div>

      <div className="space-y-2 mb-3 flex-1">
        {order.items.map((it, k) => (
          <div key={k} className="text-[15px] rounded-lg px-2 py-1.5 bg-white/[0.04] border-l-2 border-[#C69C4A]/50">
            <p className="text-white font-bold text-[17px] leading-tight">{it.quantity}× {it.name}</p>
            <div className="text-[13px] text-stone-300 pl-4 mt-0.5">
              {(it.formula || []).map((f, i) => <span key={`f${i}`} className="mr-2">· {f.course}: <b className="text-white">{f.choice}</b></span>)}
              {(it.options || []).map((o, i) => <span key={`o${i}`} className="mr-2">· {o.value}</span>)}
              {it.cooking && <span className="mr-2">· {it.cooking}</span>}
              {it.side && <span className="mr-2">· {it.side}</span>}
              {(it.supplements || []).map((s, i) => <span key={i} className="mr-2">· {s.name}</span>)}
              {it.instructions && <p className="text-[#E7C97A] italic mt-0.5 font-semibold">✎ {it.instructions}</p>}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-3 pt-2 border-t border-[#1B3028]">
        <span className="text-xs text-stone-500 uppercase tracking-wide">Total · <span className="text-[#E7C97A] font-semibold normal-case">{elapsed(order.created_at)}</span></span>
        <span className="text-lg font-bold text-[#C69C4A]">{eur(order.total)}</span>
      </div>

      <div className="flex gap-2">
        {order.status === "NOUVELLE" && (
          <>
            <Button data-testid={`btn-refuse-${num}`} onClick={stop(() => onCancel ? onCancel(order) : onStatus(order.id, "REFUSEE"))} variant="outline" className="flex-1 rounded-xl border-red-500/40 text-red-400 hover:bg-red-500/10 bg-transparent h-11">
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button data-testid={`btn-accept-${num}`} onClick={stop(() => onStatus(order.id, "ACCEPTEE"))} className="flex-1 rounded-xl bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-bold h-11">
              <Check className="w-4 h-4 mr-1" /> Accepter
            </Button>
          </>
        )}
        {order.status === "ACCEPTEE" && (
          <p className="flex-1 text-center text-sm text-[#E7C97A] py-2.5">✓ Acceptée · transmise en cuisine</p>
        )}
        {order.status === "EN_PREPARATION" && (
          <p className="flex-1 text-center text-sm text-blue-200 py-2.5">👨‍🍳 En préparation…</p>
        )}
        {order.status === "PRETE" && (
          <div className="flex-1 flex flex-col gap-2">
            <div className="flex items-center justify-between text-xs">
              <span className={readyLate ? "text-red-400 font-bold" : "text-emerald-300 font-semibold"}>🟢 Prête depuis {mmss}{readyLate ? " · à servir !" : ""}</span>
              {taken && <span data-testid={`taken-by-${num}`} className="text-[#E7C97A] font-semibold">🙋 {order.taken_for_service_by}</span>}
            </div>
            <div className="flex gap-2">
              {!taken && onTakeService && (
                <Button data-testid={`btn-take-${num}`} onClick={stop(() => onTakeService(order.id))} variant="outline" className="flex-1 rounded-xl border-[#C69C4A]/50 text-[#E7C97A] hover:bg-[#C69C4A]/10 bg-transparent h-11">
                  Prendre en charge
                </Button>
              )}
              <Button data-testid={`btn-served-${num}`} onClick={stop(() => onStatus(order.id, "SERVIE"))} className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11">
                <Utensils className="w-4 h-4 mr-1" /> Servie
              </Button>
            </div>
          </div>
        )}
        {(order.status === "SERVIE" || order.status === "REFUSEE") && (
          <p className="flex-1 text-center text-sm text-stone-500 py-2.5">Voir le détail</p>
        )}
      </div>
    </div>
  );
}

export default function OrdersBoard({ orders, onStatus, newOrderIds, slug, payments = [], onTakeService, readyAlertMin = 3 }) {
  const [ticketOrder, setTicketOrder] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [tableView, setTableView] = useState(null); // { table, session }
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase().replace("#", "");
  const searching = q.length > 0;
  const results = useMemo(() => {
    if (!searching) return [];
    return orders.filter(
      (o) =>
        o.order_number.replace("#", "").toLowerCase().includes(q) ||
        (o.order_code || "").toLowerCase().includes(q) ||
        String(o.table_number).toLowerCase().includes(q) ||
        `table ${o.table_number}`.toLowerCase().includes(q) ||
        (o.guest_name || "").toLowerCase().includes(q)
    );
  }, [orders, q, searching]);

  const active = orders.filter((o) => ["NOUVELLE", "ACCEPTEE", "EN_PREPARATION", "PRETE"].includes(o.status));
  const history = orders.filter((o) => ["SERVIE", "REFUSEE"].includes(o.status));

  const columns = [
    { key: "NOUVELLE", title: "🔔 Nouvelles", statuses: ["NOUVELLE"] },
    { key: "EN_PREPARATION", title: "👨‍🍳 En préparation", statuses: ["ACCEPTEE", "EN_PREPARATION"] },
    { key: "PRETE", title: "🟢 Prêtes à servir", statuses: ["PRETE"] },
  ];

  const detail = orders.find((o) => o.id === detailId) || null;
  const openDetail = (o) => setDetailId(o.id);
  const openTable = (o) => { setDetailId(null); setTableView({ table: o.table_number, session: o.table_session_id }); };

  const { user } = useAuth() || {};
  const canCancelInKitchen = ["admin", "manager"].includes(user?.role);
  const [editOrder, setEditOrder] = useState(null);
  const [cancelOrder, setCancelOrder] = useState(null);
  const requestCancel = (o) => {
    if (o.status !== "NOUVELLE" && !canCancelInKitchen) { toast.error("Commande déjà en cuisine : annulation réservée au Manager"); return; }
    setCancelOrder(o);
  };
  const confirmCancel = async () => { const o = cancelOrder; setCancelOrder(null); if (o) await onStatus(o.id, "REFUSEE"); };
  const cardProps = { onStatus, onTicket: setTicketOrder, onOpen: openDetail, onTableView: openTable, onTakeService, readyAlertMin, onEdit: setEditOrder, onCancel: requestCancel };

  return (
    <div>
      <OrderEditDialog open={!!editOrder} order={editOrder} slug={slug} onClose={() => setEditOrder(null)} save={(items) => api.updateOrderItems(editOrder.id, items)} />
      <Dialog open={!!cancelOrder} onOpenChange={(v) => !v && setCancelOrder(null)}>
        <DialogContent data-testid="cancel-order-dialog" className="rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100 max-w-sm">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">Êtes-vous sûr ?</DialogTitle>
            <DialogDescription className="text-stone-400">Annuler définitivement la commande {cancelOrder?.order_code || cancelOrder?.order_number} (Table {cancelOrder?.table_number}) ? La cuisine sera prévenue en temps réel.</DialogDescription></DialogHeader>
          <DialogFooter className="gap-2">
            <Button data-testid="cancel-order-no" variant="outline" onClick={() => setCancelOrder(null)} className="rounded-full bg-transparent border-[#1B3028] text-stone-300">Non, garder</Button>
            <Button data-testid="cancel-order-yes" onClick={confirmCancel} className="rounded-full bg-red-600 hover:bg-red-700 text-white font-bold">Oui, annuler</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-stone-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="orders-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher (CMD-2026-…, Table 12, prénom…)"
            className="w-full h-11 rounded-xl bg-[#111F1A] border border-[#1B3028] pl-10 pr-9 text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none focus:border-[#C69C4A]"
          />
          {searching && (
            <button data-testid="orders-search-clear" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"><X className="w-4 h-4" /></button>
          )}
        </div>
        <button data-testid="rush-fullscreen" onClick={() => { const el = document.documentElement; if (!document.fullscreenElement) el.requestFullscreen?.(); else document.exitFullscreen?.(); }} className="shrink-0 text-xs font-semibold text-[#C69C4A] hover:text-[#E7C97A] border border-[#1B3028] rounded-full px-3 py-2">⛶ Plein écran</button>
      </div>

      {searching ? (
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-stone-300 mb-3 px-1">{results.length} résultat(s) pour « {query} »</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="orders-search-results">
            {results.map((o) => (
              <OrderCard key={o.id} order={o} isNew={false} {...cardProps} />
            ))}
            {results.length === 0 && <div className="rounded-2xl border border-dashed border-[#1B3028] py-8 text-center text-xs text-stone-600 col-span-full">Aucune commande trouvée</div>}
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {columns.map((col) => {
              const list = active.filter((o) => col.statuses.includes(o.status)).sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
              return (
                <div key={col.key} data-testid={`orders-column-${col.key}`} className="min-w-0">
                  <div className="flex items-center justify-between mb-3 px-1">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-stone-300">{col.title}</h3>
                    <span className="text-xs font-bold text-stone-500 bg-[#111F1A] rounded-full px-2 py-0.5">{list.length}</span>
                  </div>
                  <div className="space-y-3">
                    {list.map((o) => (
                      <OrderCard key={o.id} order={o} isNew={newOrderIds.has(o.id)} {...cardProps} />
                    ))}
                    {list.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-[#1B3028] py-8 text-center text-xs text-stone-600">Aucune commande</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {history.length > 0 && (
            <div className="mt-8">
              <h3 className="text-sm font-bold uppercase tracking-wider text-stone-500 mb-3 px-1">Historique ({history.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                {history.slice(0, 40).map((o) => (
                  <button
                    key={o.id}
                    data-testid={`history-card-${o.order_number.replace("#", "")}`}
                    onClick={() => openDetail(o)}
                    className="text-left rounded-xl bg-[#0D1814] border border-[#1B3028] p-3 hover:border-[#C69C4A]/50 transition-colors"
                  >
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-300 font-semibold">Table {o.table_number}</span>
                      <span className={STATUS_META[o.status].color}>{STATUS_META[o.status].label}</span>
                    </div>
                    <p className="text-xs text-stone-500 mt-1">{o.order_code || o.order_number} · {eur(o.total)}{o.guest_name ? ` · ${o.guest_name}` : ""}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <KitchenTicket order={ticketOrder} onClose={() => setTicketOrder(null)} />
      <OrderDetailDrawer
        order={detail}
        open={!!detail}
        onClose={() => setDetailId(null)}
        onStatus={onStatus}
        onTicket={setTicketOrder}
        onTableView={openTable}
        payments={payments}
      />
      <TableOverviewDrawer
        open={!!tableView}
        onClose={() => setTableView(null)}
        slug={slug}
        tableNumber={tableView?.table}
        sessionId={tableView?.session}
        orders={orders}
        onOpenOrder={(o) => { setTableView(null); setDetailId(o.id); }}
      />
    </div>
  );
}
